import type { CSSProperties, ReactNode } from 'react';

/**
 * One half of a two-way segmented control (rule 2.5: tinted selection, not a
 * filled one — a solid accent segment competed with the screen's primary
 * button for "the one blue thing on this form", Brandon 2026-08-05).
 * `aria-pressed` carries the same fact to a screen reader. `zIndex: 1` on the
 * active segment is load-bearing: it keeps the active border on top of the
 * neighbor's overlapped edge (see index.css 4.2, case 4, for the sticky-bar
 * z-index this forces).
 *
 * Shared by the [T&M Weekly Rollup][Lump-Sum] / [Sent][Approved] pairs in
 * AddHistoricalPcoFlow and the [Pending][Funded] pair in BucketRowEditor —
 * previously two near-identical copies (RULES.md 3.2). The one behavioral
 * delta between them was `whiteSpace: 'nowrap'` on the historical-flow copy,
 * kept here as the `nowrap` prop.
 */
export interface SegButtonProps {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  position: 'left' | 'right';
  children: ReactNode;
  /** Keeps the label on one line where the segment sits in a
   *  width-squeezed row (AddHistoricalPcoFlow's pairs need it;
   *  BucketRowEditor's don't). */
  nowrap?: boolean;
}

export function SegButton({
  active,
  disabled,
  onClick,
  position,
  children,
  nowrap,
}: SegButtonProps) {
  const style: CSSProperties = {
    fontFamily: 'var(--sans)',
    fontSize: 12.5,
    padding: '5px 12px',
    cursor: disabled ? 'default' : 'pointer',
    border: active ? '1px solid var(--accent)' : '1px solid var(--rule)',
    borderTopLeftRadius: position === 'left' ? 6 : 0,
    borderBottomLeftRadius: position === 'left' ? 6 : 0,
    borderTopRightRadius: position === 'right' ? 6 : 0,
    borderBottomRightRadius: position === 'right' ? 6 : 0,
    marginLeft: position === 'right' ? -1 : 0,
    background: active ? 'var(--accent-tint-08)' : 'var(--surface)',
    color: active ? 'var(--accent)' : 'var(--ink-2)',
    fontWeight: active ? 500 : 400,
    position: 'relative',
    zIndex: active ? 1 : 0,
    ...(nowrap ? { whiteSpace: 'nowrap' as const } : {}),
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      style={style}
    >
      {children}
    </button>
  );
}
